import {
  Button,
  Chip,
  Grid,
  Popover,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Alert,
  Collapse,
} from '@mui/material'
import {FormProvider, RHFSelectField, RHFTextField} from '../../common/hook-form'
import {LoadingButton} from '@mui/lab'
import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {downloadCSV, formatCurrency, formatDate, formatNumber} from '../../helpers/UtilityHelper'
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp'
import {apiConstants, post, postGeneric} from '../../common/constants'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import {getMerchant} from '../../helpers/ReduxHelpers'
import {getYear} from 'date-fns'
import MUIModal from '../../common/MUIModal'
import _ from 'lodash'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import PageTitle from '../../common/PageTitle'
import {useSnackbar} from 'notistack'
import {Receipt, Info} from '@mui/icons-material'
import {filterBarSx, primaryBtnSx, secondaryBtnSx} from '../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../styles/foundation/colors'
import {primaryTextSx, secondaryTextSx, cellStackSx} from '../orders/list2/cellStyles'

const Status = [
  {label: 'GENERATED', value: 'GENERATED'},
  {label: 'REMITTED', value: 'REMITTED'},
  {label: 'APPROVED', value: 'APPROVED'},
  {label: 'PARTIALLY PAID', value: 'PARTIALLY_PAID'},
]

const Month = [
  {label: 'January', value: '01'},
  {label: 'February', value: '02'},
  {label: 'March', value: '03'},
  {label: 'April', value: '04'},
  {label: 'May', value: '05'},
  {label: 'June', value: '06'},
  {label: 'July', value: '07'},
  {label: 'August', value: '08'},
  {label: 'September', value: '09'},
  {label: 'October', value: '10'},
  {label: 'November', value: '11'},
  {label: 'December', value: '12'},
]

const STATUS_COLORS = {
  GENERATED: {bgcolor: '#e8f5e9', color: '#2e7d32'},
  REMITTED: {bgcolor: '#e3f2fd', color: '#1565c0'},
  PARTIALLY_PAID: {bgcolor: '#fce4ec', color: '#c62828'},
  APPROVED: {bgcolor: '#fff3e0', color: '#e65100'},
}

export function CodRemittance() {
  const merchant = getMerchant()
  const merchantId = merchant.id ? merchant.id : ''

  const [data, setData] = useState([])
  const [filterData, setFilterData] = useState({
    merchantId: merchantId,
    yearAndMonth: null,
    year: null,
    createdDateSort: 'DESC',
  })

  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [reload, setReload] = useState(0)
  const [rowCount, setRowCount] = useState()
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [rows, setRow] = useState([])
  const {enqueueSnackbar} = useSnackbar()
  const [remarkPopover, setRemarkPopover] = useState({anchor: null, remark: null})
  const [showInfo, setShowInfo] = useState(false)

  const columns = [
    {
      field: 'Period',
      headerName: 'Settlement Period',
      flex: 1.5,
      minWidth: 230,
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>
          {`${formatDate(new Date(row.fromDate), 'dd MMM yyyy')} - ${formatDate(
            new Date(row.toDate),
            'dd MMM yyyy'
          )}`}
        </Typography>
      ),
    },
    {
      field: 'Total Orders',
      headerName: 'Total Orders',
      align: 'right',
      headerAlign: 'right',
      flex: 0.8,
      minWidth: 120,
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{formatNumber(row?.totalOrders)}</Typography>
      ),
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 1,
      minWidth: 140,
      renderCell: ({row}) => {
        const colors = STATUS_COLORS[row.reportStage] || {bgcolor: '#f5f5f5', color: '#616161'}
        return (
          <Chip
            label={row.reportStage}
            size='small'
            sx={{
              fontFamily: FONT,
              fontSize: '11px',
              fontWeight: WEIGHT.medium,
              height: 24,
              ...colors,
            }}
          />
        )
      },
    },
    {
      field: 'COD Amount Payable',
      headerName: 'COD Amount',
      flex: 1,
      minWidth: 140,
      renderCell: ({row}) => (
        <Typography sx={{...primaryTextSx, color: NAVY}}>
          {formatCurrency(row?.totalCODAmount)}
        </Typography>
      ),
    },
    {
      field: 'Remitted Amount',
      headerName: 'Remitted Amount',
      flex: 1,
      minWidth: 150,
      renderCell: ({row}) => (
        <Typography
          sx={{...secondaryTextSx, color: row?.amountRemittedToMerchant ? '#2e7d32' : SUBTLE}}
        >
          {row?.amountRemittedToMerchant
            ? formatCurrency(row?.amountRemittedToMerchant)
            : 'Not Remitted'}
        </Typography>
      ),
    },
    {
      field: 'Action',
      headerName: 'Actions',
      flex: 1.2,
      minWidth: 200,
      renderCell: ({row}) => {
        if (row?.totalOrders === 0) return null
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Tooltip title='Download COD Report'>
              <IconButton
                onClick={() => window.open(row.reportSignedLinkS3)}
                color='primary'
                size='small'
              >
                <Receipt fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Download CSV'>
              <IconButton onClick={() => handleCodDownload(row)} color='success' size='small'>
                <DownloadSharpIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            {row?.merchantPaymentDetails?.length > 0 && (
              <Tooltip title='Payment Details'>
                <IconButton
                  onClick={() => handleFinance(row)}
                  size='small'
                  sx={{color: '#e65100'}}
                >
                  <CurrencyRupeeIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {row?.remark && (
              <Chip
                icon={<Info sx={{fontSize: '14px !important'}} />}
                label='Remark'
                size='small'
                onClick={(e) =>
                  setRemarkPopover({anchor: e.currentTarget, remark: row.remark})
                }
                sx={{
                  cursor: 'pointer',
                  fontFamily: FONT,
                  fontSize: '11px',
                  fontWeight: WEIGHT.semibold,
                  bgcolor: '#fff3e0',
                  color: '#e65100',
                  border: '1px solid #ffe0b2',
                }}
              />
            )}
          </Box>
        )
      },
    },
  ]

  const handleCodDownload = (data) => {
    let payload = {
      fromDate: formatDate(data.fromDate, 'yyyy-MM-dd'),
      toDate: formatDate(data.toDate, 'yyyy-MM-dd'),
      merchantId: data.merchantRef,
    }
    setLoading(true)
    postGeneric(`${apiConstants.COD_REMITTANCE_REPORT}`, payload)
      .then((res) => {
        setLoading(false)
        if (res.status === 200 && res.data.status === 'ERROR') {
          enqueueSnackbar(res.data.errorMsg, {variant: 'error'})
        } else {
          let csvData = res.data
          const filename = 'cod_remittance.csv'
          downloadCSV(csvData, filename)
        }
      })
      .catch((err) => {
        setLoading(false)
        console.error(err)
        enqueueSnackbar('Some error occurred', {variant: 'error'})
      })
  }

  const handleFinance = (row) => {
    setOpen(true)
    setRow(row)
  }

  const methods = useForm({
    defaultValues: {
      merchant: '',
      status: '',
      year: '',
      month: '',
    },
  })

  const {handleSubmit, reset} = methods

  const onSubmit = (data) => {
    var newDate = ''
    if (data.year && data.month) {
      newDate = data.year + data.month
    }
    if (data.month) {
      const date = getYear(new Date())
      newDate = date + data.month
    }
    setFilterData({
      merchantId: merchantId,
      yearAndMonth: newDate,
      year: data.year,
      createdDateSort: 'DESC',
      reportStageIn: data.status,
    })
  }

  useEffect(() => {
    setLoading(true)
    filterData.skip = page * sizePerPage
    filterData.limit = sizePerPage
    post(`${apiConstants.BASE_LOCAL}${apiConstants.COD_MERCHANT_REPORT}`, filterData)
      .then((res) => {
        setLoading(false)
        let d = res.data.responseObj
        const filteredRecords = _.filter(d, (record) => {
          const fromDate = new Date(record.fromDate)
          const firstMay = new Date('2023-05-01T00:00:00.000+00:00')
          return fromDate > firstMay
        })
        setData(filteredRecords)
      })
      .catch((err) => {
        setLoading(false)
        console.error(err)
      })
  }, [reload, sizePerPage, page, filterData])

  useEffect(() => {
    filterData.skip = 0
    filterData.limit = 10000
    post(`${apiConstants.BASE_LOCAL}${apiConstants.COD_MERCHANT_REPORT}`, filterData)
      .then((res) => {
        setRowCount(res?.data?.responseObj)
      })
      .catch((err) => console.error(err))
  }, [reload, filterData])

  const paymentColumns = [
    {
      field: 'bankName',
      headerName: 'Bank Name',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => <Typography sx={primaryTextSx}>{params.value}</Typography>,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography sx={{...primaryTextSx, color: NAVY}}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'paymentMode',
      headerName: 'Payment Mode',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size='small'
          variant='outlined'
          sx={{fontFamily: FONT, fontSize: '11px'}}
        />
      ),
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Typography sx={secondaryTextSx}>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: 'utrNo',
      headerName: 'UTR Number',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography sx={{fontFamily: 'monospace', fontSize: SIZE.xs, color: NAVY}}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'remark',
      headerName: 'Remark',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Typography
            sx={{
              ...secondaryTextSx,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value || '\u2014'}
          </Typography>
        </Tooltip>
      ),
    },
  ]

  const codRemittanceConfig = merchant.codRemittanceConfig || {}

  return (
    <>
      <PageTitle>COD Remittance</PageTitle>

      <Box sx={filterBarSx}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} sm={6} md={3}>
              <RHFSelectField
                size='small'
                name='status'
                label='Settlement Status'
                options={Status.map((val) => ({
                  value: val.value,
                  label: val.label,
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RHFSelectField
                size='small'
                name='month'
                label='Month'
                options={Month.map((val) => ({
                  value: val.value,
                  label: val.label,
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <RHFTextField name='year' label='Year' size='small' />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <LoadingButton
                loading={loading}
                type='submit'
                variant='contained'
                size='small'
                sx={primaryBtnSx}
              >
                Search
              </LoadingButton>
              <Button
                onClick={() => {
                  reset({status: '', month: '', year: ''})
                  setFilterData({
                    merchantId: merchantId,
                    yearAndMonth: null,
                    year: null,
                    createdDateSort: 'DESC',
                  })
                  setReload(reload + 1)
                }}
                variant='contained'
                size='small'
                sx={secondaryBtnSx}
              >
                Reset
              </Button>
              <Tooltip title='About COD Remittances'>
                <IconButton size='small' onClick={() => setShowInfo((v) => !v)} sx={{color: showInfo ? NAVY : SUBTLE}}>
                  <Info fontSize='small' />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </FormProvider>
      </Box>

      <Collapse in={showInfo}>
        <Alert severity='info' sx={{my: 1.5, fontFamily: FONT, fontSize: SIZE.xs}} onClose={() => setShowInfo(false)}>
          COD remittances are processed weekly. You can track the status of each settlement
          period, download detailed reports, and view payment information. Remittances typically
          take 3-7 business days to process.
          <br /><br />
          <strong>COD Remittance Cycle:</strong> {codRemittanceConfig.cycleOfDays || 'N/A'} days
          <br />
          <strong>Early COD Enabled:</strong> {codRemittanceConfig.earlyCODEnabled ? 'Yes' : 'No'}
        </Alert>
      </Collapse>

      <Box sx={{mt: 1.5}}>
        <ProzoDataGrid
          columns={columns}
          autoHeight={true}
          rows={data || []}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          filterMode='server'
          rowCount={rowCount?.length || 0}
          getRowId={(row) => row.id}
        />
      </Box>

      <MUIModal
        open={open}
        handleClose={() => setOpen(false)}
        title='Payment Remittance Details'
        maxWidth='md'
        fullWidth
      >
        <ProzoDataGrid
          columns={paymentColumns}
          autoHeight={true}
          rows={rows?.merchantPaymentDetails || []}
          rowCount={rows?.merchantPaymentDetails?.length || 0}
          getRowId={(row) => `${row.utrNo}-${row.paymentDate}-${row.amount}`}
          hideFooterPagination={true}
          paginationMode='client'
        />
      </MUIModal>

      <Popover
        open={Boolean(remarkPopover.anchor)}
        anchorEl={remarkPopover.anchor}
        onClose={() => setRemarkPopover({anchor: null, remark: null})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        transformOrigin={{vertical: 'top', horizontal: 'center'}}
        PaperProps={{sx: {width: 400, borderRadius: '10px', overflow: 'hidden'}}}
      >
        <Box
          sx={{
            px: 2,
            py: 1.2,
            bgcolor: NAVY,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Info sx={{color: 'white', fontSize: 16}} />
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: SIZE.xs,
              fontWeight: WEIGHT.semibold,
              color: 'white',
            }}
          >
            Remark
          </Typography>
        </Box>
        <Box sx={{p: 2}}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: SIZE.xs,
              color: NAVY,
              lineHeight: 1.8,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {remarkPopover.remark}
          </Typography>
        </Box>
      </Popover>
    </>
  )
}
